import {
  HomePageQuery,
  CategoryPageQuery,
  ArticlePageQuery,
} from '@elevio-kb/queries/dist/types';
import { Actions } from '../actions/types';
import {
  ActionTypes as PageActionTypes,
  Actions as PageActions,
} from '../actions/page';

export type ErrorStatus =
  | 'HasError'
  | 'NotAllowedError'
  | 'NotFound'
  | 'LoginRequired';

export type FetchingStatus = ErrorStatus | 'has-fetched';

type HomePage = {
  pageType: 'home';
};

type CategoryPage = {
  pageType: 'category';
  categoryId: string;
};

type ArticlePage = {
  pageType: 'article';
  articleId: string;
};

type SearchPage = {
  pageType: 'search';
  searchTerm: string;
};

type ExtraPage = {
  pageType: 'extra';
};

type ErrorPage = {
  pageType: 'error';
};

type Error401Page = {
  pageType: 'error401';
};

type Error404Page = {
  pageType: 'error404';
};

type LoadingPageType =
  | HomePage
  | CategoryPage
  | ArticlePage
  | SearchPage
  | ExtraPage;

type WithStatus<P, D> = P &
  (
    | {
        status: 'has-fetched';
        path: string;
        data: D;
      }
    | {
        status: FetchingStatus;
        path: string;
        data: null;
      }
  );

type CurrentPageType =
  | WithStatus<HomePage, HomePageQuery>
  | WithStatus<CategoryPage, CategoryPageQuery>
  | WithStatus<ArticlePage, ArticlePageQuery>
  | WithStatus<SearchPage, null>
  | WithStatus<ExtraPage, null>
  | WithStatus<Error401Page, null>
  | WithStatus<Error404Page, null>
  | WithStatus<ErrorPage, null>;

export type IState = Readonly<{
  currentPage: CurrentPageType | null;
  loadingPage: LoadingPageType | null;
}>;

export const initialState: IState = {
  currentPage: null,
  loadingPage: null,
};

const reducer = (state: IState = initialState, action: Actions): IState => {
  switch (action.type) {
    case PageActionTypes.PAGE_NAV_START:
      return pageNavStart(state, action);

    case PageActionTypes.PAGE_NAV_COMPLETE:
      return pageNavComplete(state, action);

    case PageActionTypes.PAGE_NAV_ERROR:
      return pageNavError(state, action);
  }
  return state;
};

function pageNavStart(
  state: IState,
  action: ReturnType<typeof PageActions.pageNavStart>
): IState {
  const payload = action.payload;

  const returnState = (newPage: LoadingPageType): IState => ({
    ...state,
    loadingPage: newPage,
  });

  switch (payload.pageType) {
    case 'article':
      return returnState({
        pageType: 'article',
        articleId: payload.articleId,
      });

    case 'category': {
      return returnState({
        pageType: 'category',
        categoryId: payload.categoryId,
      });
    }

    case 'home': {
      return returnState({
        pageType: 'home',
      });
    }

    case 'search': {
      return returnState({
        pageType: 'search',
        searchTerm: payload.searchTerm,
      });
    }

    default:
      throwPageType(payload);
  }

  return state;
}

export function pageNavComplete(
  state: IState,
  action: ReturnType<typeof PageActions.pageNavComplete>
): IState {
  const payload = action.payload;

  const returnState = (newPage: CurrentPageType): IState => ({
    ...state,
    currentPage: newPage,
    loadingPage: null,
  });

  switch (payload.pageType) {
    case 'article':
      return returnState({
        pageType: 'article',
        articleId: payload.articleId,
        status: 'has-fetched',
        path: payload.path,
        data: payload.data,
      });

    case 'category':
      return returnState({
        pageType: 'category',
        categoryId: payload.categoryId,
        status: 'has-fetched',
        path: payload.path,
        data: payload.data,
      });

    case 'home':
      return returnState({
        pageType: 'home',
        status: 'has-fetched',
        path: payload.path,
        data: payload.data,
      });

    case 'search':
      return returnState({
        pageType: 'search',
        searchTerm: payload.searchTerm,
        status: 'has-fetched',
        path: payload.path,
        data: null,
      });

    case 'error404':
      return returnState({
        pageType: 'error404',
        status: 'has-fetched',
        path: payload.path,
        data: null,
      });

    case 'error401':
      return returnState({
        pageType: 'error401',
        status: 'has-fetched',
        path: payload.path,
        data: null,
      });

    case 'extra':
      return returnState({
        pageType: 'extra',
        status: 'has-fetched',
        path: payload.path,
        data: null,
      });

    default:
      throwPageType(payload);
  }

  return state;
}

export function pageNavError(
  state: IState,
  action: ReturnType<typeof PageActions.pageNavError>
): IState {
  const payload = action.payload;

  const returnState = (newPage: CurrentPageType): IState => ({
    ...state,
    currentPage: newPage,
    loadingPage: null,
  });

  switch (payload.pageType) {
    case 'article':
      return returnState({
        pageType: 'article',
        articleId: payload.articleId,
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    case 'category':
      return returnState({
        pageType: 'category',
        categoryId: payload.categoryId,
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    case 'home':
      return returnState({
        pageType: 'home',
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    case 'search':
      return returnState({
        pageType: 'search',
        searchTerm: payload.searchTerm,
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    case 'extra':
      return returnState({
        pageType: 'extra',
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    case 'error':
      return returnState({
        pageType: 'error',
        path: payload.path,
        status: payload.errorStatus,
        data: null,
      });

    default:
      throwPageType(payload);
  }

  return state;
}

function throwPageType(p: never): never;
function throwPageType(p: any) {
  throw new Error('Unknown page type: ' + p);
}

export default reducer;
